import { Grid } from '@mui/material';
import React from 'react';
import styles from './styles.module.css';
import { PortfolioStatisticsLineChart } from 'pages/Cases/components/PortfolioStatisticsLineChart';
import {
  filterPerformance,
  portfolioStatisticsChartFilter,
} from 'pages/Cases/PortfolioConstruction/Summary/PerformanceReport';
import { ModelSetPerformance } from 'features/premium-report/api/types';
import { calculateDateRange } from 'features/premium-report/utils';
import { getSlideField } from '../../../utils';
import { Slide } from '../../../_data';
import { SlideSubtitle } from '../slide-subtitle/slide-subtitle';
import { SlideParagraph } from '../slide-paragraph';
import { EditableField } from '../../../components/EditableField';

export type ExcessReturnPerformanceProps = [
  {
    field: 'modelSetPerformance';
    label: string;
    value: ModelSetPerformance[];
    type: 'modelSetPerformance';
  },
  {
    field: 'excessReturnAnalysis';
    label: string;
    value: string;
    type: 'excessReturnAnalysis';
  },
  {
    field: 'profile';
    label: string;
    value: number;
    type: 'number';
  },
];
export const ExcessReturnPerformance = React.memo(
  ({
    slide,
    onChangeSlide,
  }: {
    slide: Slide<ExcessReturnPerformanceProps>;
    onChangeSlide: (
      slide: Slide<ExcessReturnPerformanceProps>,
      content: ExcessReturnPerformanceProps,
    ) => void;
  }): React.ReactElement => {
    const modelSetPerformance = getSlideField(
      slide,
      'modelSetPerformance',
    )?.value;
    const profile = getSlideField(slide, 'profile')?.value;
    const option = [modelSetPerformance[0].portfolio_options[0].option];
    const filteredPerformance = filterPerformance(
      modelSetPerformance,
      [],
      option,
      profile,
    );

    if (!filteredPerformance || filteredPerformance.length === 0) {
      return (
        <Grid container direction="column" className={styles.container}>
          <Grid item>
            <SlideSubtitle>Excess Return Analysis</SlideSubtitle>
            <SlideParagraph>
              No data available to display excess return analysis.
            </SlideParagraph>
          </Grid>
        </Grid>
      );
    }

    const oneYearDateRange =
      calculateDateRange(
        filteredPerformance,
        'rolling_one_year_stats',
        '1_year_excess_returns',
        '12',
      ) || 'Rango de fechas no disponible';

    const threeYearDateRange =
      calculateDateRange(
        filteredPerformance,
        'rolling_three_year_stats',
        '3_year_excess_returns',
        '36',
      ) || 'Rango de fechas no disponible';

    return (
      <Grid container direction="column" className={styles.container}>
        <Grid item>
          <SlideSubtitle>
            {' '}
            Excess Return Analysis - 1-year and 3-year Periods
          </SlideSubtitle>
          <SlideParagraph>
            <b>Model vs Benchmark: Excess Return</b>
          </SlideParagraph>
          <SlideParagraph>{oneYearDateRange}</SlideParagraph>
          <Grid item>
            <PortfolioStatisticsLineChart
              data={portfolioStatisticsChartFilter({
                filteredPerformance: filteredPerformance,
                name: 'rolling_one_year_stats',
                subName: '1_year_excess_returns',
              })}
              height={300}
              fontSize={14}
              formatChart={{
                unit: '%',
                decimals: 2,
                prefix: '',
                value: 'Excess Return',
                label: 'Excess Return',
              }}
              isWithAnimation={false}
            />
          </Grid>
        </Grid>
        <Grid item>
          <SlideParagraph>
            <b>Model vs Benchmark: Excess Return</b>
          </SlideParagraph>
          <SlideParagraph>{threeYearDateRange}</SlideParagraph>
          <Grid item>
            <PortfolioStatisticsLineChart
              data={portfolioStatisticsChartFilter({
                filteredPerformance: filteredPerformance,
                name: 'rolling_three_year_stats',
                subName: '3_year_excess_returns',
              })}
              height={300}
              fontSize={14}
              formatChart={{
                unit: '%',
                decimals: 2,
                prefix: '',
                value: 'Excess Return',
                label: 'Excess Return',
              }}
              isWithAnimation={false}
            />
          </Grid>
        </Grid>
        <Grid item>
          <SlideParagraph
            style={{
              marginBottom: '10px',
              marginTop: '10px',
              fontSize: '13px',
            }}
          >
            Source: TIFIN Sage
          </SlideParagraph>
        </Grid>
        <Grid item>
          <SlideSubtitle style={{ marginBottom: '10px', fontSize: '20px' }}>
            Rolling period excess returns
          </SlideSubtitle>
          <EditableField
            field="excessReturnAnalysis"
            slide={slide}
            onSlideContentChange={onChangeSlide}
            style={{
              fontFamily: 'Roboto',
              fontWeight: '400',
              fontSize: '15px',

              color: '#353536',
            }}
          />
        </Grid>
      </Grid>
    );
  },
);
